/**
 * "Added to your cart" confirmation (Figma: Lo-La UI, node 4387-7596).
 * Mobile: bottom sheet. Desktop: right-side slider.
 * Shows the added item, free-shipping progress and "Recommended for you".
 *
 * theme.js ProductForm calls window.AddedToCart.show() after a successful
 * add and falls back to opening the cart drawer when it returns false.
 */
(function () {
  const RECS_SECTION = "added-to-cart-recommendations";

  function formatMoney(cents) {
    if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.cartStrings?.money_format);
    }
    return (cents / 100).toFixed(2);
  }

  function getAddedItem(response, variantId) {
    if (response?.items?.length) {
      return (
        response.items.find(
          (item) => String(item.variant_id) === String(variantId)
        ) || response.items[response.items.length - 1]
      );
    }
    if (response?.id || response?.variant_id) return response;
    return null;
  }

  class AddedToCartSheet extends HTMLElement {
    constructor() {
      super();
      this._cart = null;
      this._recsProductId = null;
      this._innerSubmitAt = 0;
      this._onKeyDown = this._onKeyDown.bind(this);
    }

    connectedCallback() {
      this.querySelectorAll("[data-atc-close]").forEach((el) => {
        el.addEventListener("click", () => this.close());
      });
      this.querySelector("[data-atc-view-cart]")?.addEventListener(
        "click",
        () => this._openCart()
      );
      // A card inside the sheet was added: refresh the item and shipping bar,
      // but keep the recommendations the shopper is browsing.
      this.addEventListener(
        "submit",
        () => {
          this._innerSubmitAt = Date.now();
        },
        true
      );
    }

    _onKeyDown(e) {
      if (e.key === "Escape") this.close();
    }

    show(response, cart, variantId) {
      const item = getAddedItem(response, variantId);
      if (!item) return false;

      const wasOpen = this.classList.contains("is-open");
      this._cart = cart;
      this._renderItem(item);
      this._refreshShipping();
      const fromSheet = Date.now() - this._innerSubmitAt < 15000;
      this._innerSubmitAt = 0;
      if (!fromSheet) this._loadRecommendations(item.product_id);

      if (!wasOpen) {
        this.removeAttribute("hidden");
        this.setAttribute("aria-hidden", "false");
        void this.offsetWidth; // let the open transition run after un-hiding
        this.classList.add("is-open");
        document.documentElement.classList.add("atc-sheet-open");
        document.addEventListener("keydown", this._onKeyDown);
        this.querySelector(".atc-sheet__panel")?.focus({ preventScroll: true });
      }
      this.querySelector(".atc-sheet__body")?.scrollTo({ top: 0, behavior: "smooth" });
      return true;
    }

    _renderItem(item) {
      const imageEl = this.querySelector("[data-atc-image]");
      const nameEl = this.querySelector("[data-atc-name]");
      const vendorEl = this.querySelector("[data-atc-vendor]");
      const priceEl = this.querySelector("[data-atc-price]");
      const compareEl = this.querySelector("[data-atc-compare]");
      const variantEl = this.querySelector("[data-atc-variant]");

      const imageUrl =
        item.featured_image?.url ||
        item.image ||
        (typeof item.featured_image === "string" ? item.featured_image : "");
      const title = item.product_title || item.title || "";
      const price = item.final_price ?? item.price ?? 0;
      const compare = item.original_price ?? item.price ?? 0;
      const variantTitle =
        item.variant_title && item.variant_title !== "Default Title"
          ? item.variant_title
          : "";

      if (imageEl && imageUrl) {
        imageEl.src = imageUrl;
        imageEl.alt = title;
      }
      nameEl.textContent = title;
      vendorEl.textContent = item.vendor || "";
      vendorEl.hidden = !item.vendor;
      priceEl.textContent = formatMoney(price);
      compareEl.textContent = compare > price ? formatMoney(compare) : "";
      compareEl.hidden = !(compare > price);
      variantEl.textContent = variantTitle;
      variantEl.hidden = !variantTitle;
    }

    async _refreshShipping() {
      const wrap = this.querySelector("[data-atc-shipping]");
      const min = Number(this.dataset.freeShippingMin);
      const rate = Number(window.Shopify?.currency?.rate || 1);
      if (!wrap || !min) return;

      let cart;
      try {
        cart = await fetch(`${window.routes?.cart_url || "/cart"}.js`).then(
          (res) => res.json()
        );
      } catch (e) {
        return;
      }

      const threshold = min * rate * 100;
      const subtotal = cart.items_subtotal_price || 0;
      const reached = subtotal >= threshold;
      const progress = Math.min(100, (subtotal / threshold) * 100);

      this.querySelector("[data-atc-shipping-text]").textContent = reached
        ? this.dataset.freeShippingReached || ""
        : (this.dataset.freeShippingAway || "").replace(
            "[amount]",
            formatMoney(threshold - subtotal)
          );
      wrap.classList.toggle("is-free", reached);
      wrap.hidden = false;
      this.querySelector("[data-atc-shipping-track]")?.setAttribute(
        "aria-valuenow",
        Math.round(progress)
      );
      const bar = this.querySelector("[data-atc-shipping-bar]");
      requestAnimationFrame(() => {
        bar.style.width = `${progress}%`;
      });
    }

    async _loadRecommendations(productId) {
      const wrap = this.querySelector("[data-atc-recs-wrap]");
      const list = this.querySelector("[data-atc-recs]");
      const baseUrl = this.dataset.recommendationsUrl;
      if (!wrap || !list || !baseUrl || !productId) return;
      if (this._recsProductId === productId && list.children.length) return;

      this._recsProductId = productId;
      wrap.hidden = true;
      list.innerHTML = "";

      try {
        const url = `${baseUrl}?product_id=${productId}&limit=8&section_id=${RECS_SECTION}`;
        const html = await fetch(url).then((res) => res.text());
        if (this._recsProductId !== productId) return;
        const doc = new DOMParser().parseFromString(html, "text/html");
        const cards = doc.querySelectorAll(".atc-sheet-recs > .product-item");
        if (!cards.length) return;
        cards.forEach((card) => list.appendChild(document.adoptNode(card)));
        wrap.hidden = false;
      } catch (e) {
        console.error(e);
      }
    }

    close() {
      this.classList.remove("is-open");
      this.setAttribute("aria-hidden", "true");
      document.documentElement.classList.remove("atc-sheet-open");
      document.removeEventListener("keydown", this._onKeyDown);
      setTimeout(() => {
        if (!this.classList.contains("is-open")) this.setAttribute("hidden", "");
      }, 400);
    }

    _openCart() {
      this.close();
      if (this._cart?.open) {
        this._cart.open();
      } else {
        window.location.href = window.routes?.cart_url || "/cart";
      }
    }
  }

  if (!customElements.get("added-to-cart")) {
    customElements.define("added-to-cart", AddedToCartSheet);
  }

  window.AddedToCart = {
    // Every page except the cart page, which updates its line items in place.
    shouldShow() {
      return !document.body.classList.contains("template-cart");
    },
    show(response, cart, variantId) {
      if (!this.shouldShow()) return false;
      const sheet = document.querySelector("added-to-cart");
      return sheet ? sheet.show(response, cart, variantId) : false;
    },
  };
})();
